import React, { useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    Button,
    Row,
    Col,
    Divider,
    Typography,
    message,
    Space,
    Collapse,
    theme,
    Grid
} from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { Contractor, ContractorStatus, CreateContractorDto } from '../types/contractor';
import { usersApi, crmApi } from '../../../api/client';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;
const { Panel } = Collapse;

interface ContractorFormProps {
    open: boolean;
    onClose: () => void;
    contractor?: Contractor;
    onSubmit: () => void;
}

export const ContractorForm: React.FC<ContractorFormProps> = ({ open, onClose, contractor, onSubmit }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = React.useState(false);
    const [users, setUsers] = React.useState<any[]>([]);
    const [usersLoading, setUsersLoading] = React.useState(false);
    const { token } = theme.useToken();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;

    useEffect(() => {
        const fetchUsers = async () => {
            setUsersLoading(true);
            try {
                const response = await usersApi.getAll();
                setUsers(response.data);
            } catch (error) {
                console.error('Failed to fetch users', error);
                message.error('Nie udało się pobrać listy użytkowników');
            } finally {
                setUsersLoading(false);
            }
        };

        if (open) {
            fetchUsers();
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            if (contractor) {
                form.setFieldsValue({
                    name: contractor.name,
                    nip: contractor.nip,
                    emails: contractor.emails?.length ? contractor.emails : [''],
                    phones: contractor.phones?.length ? contractor.phones : [''],
                    status: contractor.status,
                    accountManagerId: contractor.accountManagerId,
                    tags: contractor.tags,
                    invoiceAddress: contractor.invoiceAddress || { street: '', city: '', postalCode: '', country: 'Polska' },
                    deliveryAddress: contractor.deliveryAddress || { street: '', city: '', postalCode: '', country: 'Polska' },
                    paymentTerms: contractor.paymentTerms,
                    fulfillmentNotes: contractor.fulfillmentNotes,
                    description: contractor.description,
                    website: contractor.website
                });
            } else {
                form.resetFields();
                form.setFieldsValue({
                    status: ContractorStatus.POTENTIAL,
                    emails: [''],
                    phones: [''],
                    invoiceAddress: { street: '', city: '', postalCode: '', country: 'Polska' },
                    deliveryAddress: { street: '', city: '', postalCode: '', country: 'Polska' },
                });
            }
        }
    }, [contractor, open, form]);

    const handleSubmit = async (values: any) => {
        setLoading(true);
        // Clean up empty emails/phones
        const cleanedValues = {
            ...values,
            emails: values.emails?.filter((e: string) => !!e) || [],
            phones: values.phones?.filter((p: string) => !!p) || [],
        };

        try {
            if (contractor) {
                await crmApi.updateContractor(contractor.id, cleanedValues);
                message.success('Zaktualizowano kontrahenta');
            } else {
                await crmApi.createContractor(cleanedValues as CreateContractorDto);
                message.success('Utworzono kontrahenta');
            }
            onSubmit();
        } catch (error) {
            console.error('Failed to save contractor', error);
            message.error('Błąd zapisu kontrahenta');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={contractor ? 'Edytuj kontrahenta' : 'Dodaj kontrahenta'}
            open={open}
            onCancel={onClose}
            footer={null}
            width={isMobile ? '100%' : 800}
            style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : { top: 20 }}
            bodyStyle={isMobile ? { padding: '12px' } : {}}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
            >
                <Row gutter={16}>
                    <Col xs={24} md={10}>
                        <Form.Item
                            name="name"
                            label="Nazwa firmy"
                            rules={[{ required: true, message: 'Podaj nazwę' }]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={7}>
                        <Form.Item
                            name="nip"
                            label="NIP"
                        >
                            <Input placeholder="np. 1234567890" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={7}>
                        <Form.Item
                            name="website"
                            label="Strona WWW"
                        >
                            <Input placeholder="www.firma.pl" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="description"
                    label="Opis firmy"
                >
                    <TextArea rows={2} placeholder="Krótki opis profilu działalności..." />
                </Form.Item>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item label="Adresy Email">
                            <Form.List name="emails">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map((field, index) => (
                                            <Form.Item
                                                required={false}
                                                key={field.key}
                                                style={{ marginBottom: 8 }}
                                            >
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                    <Form.Item
                                                        {...field}
                                                        noStyle
                                                    >
                                                        <Input placeholder="email@firma.pl" prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} style={{ flex: 1 }} />
                                                    </Form.Item>
                                                    {fields.length > 1 && (
                                                        <Button 
                                                            type="text" 
                                                            danger 
                                                            icon={<DeleteOutlined />} 
                                                            onClick={() => remove(field.name)} 
                                                        />
                                                    )}
                                                </div>
                                            </Form.Item>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small" block={isMobile}>
                                            Dodaj email
                                        </Button>
                                    </>
                                )}
                            </Form.List>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item label="Telefony">
                            <Form.List name="phones">
                                {(fields, { add, remove }) => (
                                    <>
                                        {fields.map((field, index) => (
                                            <Form.Item
                                                required={false}
                                                key={field.key}
                                                style={{ marginBottom: 8 }}
                                            >
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                    <Form.Item
                                                        {...field}
                                                        noStyle
                                                    >
                                                        <Input placeholder="+48 123 456 789" prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />} style={{ flex: 1 }} />
                                                    </Form.Item>
                                                    {fields.length > 1 && (
                                                        <Button 
                                                            type="text" 
                                                            danger 
                                                            icon={<DeleteOutlined />} 
                                                            onClick={() => remove(field.name)} 
                                                        />
                                                    )}
                                                </div>
                                            </Form.Item>
                                        ))}
                                        <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} size="small" block={isMobile}>
                                            Dodaj telefon
                                        </Button>
                                    </>
                                )}
                            </Form.List>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="status"
                            label="Status"
                            initialValue={ContractorStatus.POTENTIAL}
                        >
                            <Select
                                options={Object.values(ContractorStatus).map((status) => ({
                                    label: status,
                                    value: status,
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="accountManagerId"
                            label="Opiekun (Account Manager)"
                        >
                            <Select
                                loading={usersLoading}
                                placeholder="Wybierz opiekuna"
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={Array.isArray(users) ? users.map((user: any) => ({
                                    label: `${user.firstName} ${user.lastName}`,
                                    value: user.id,
                                })) : []}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {!contractor && (
                    <div style={{ marginBottom: 24 }}>
                        <Collapse ghost>
                            <Panel header={<Space><UserOutlined /><span>Dodaj osobę kontaktową (opcjonalnie)</span></Space>} key="1">
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name={['primaryContact', 'firstName']} label="Imię">
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name={['primaryContact', 'lastName']} label="Nazwisko">
                                            <Input />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name={['primaryContact', 'email']} label="Email osoby">
                                            <Input prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name={['primaryContact', 'phone']} label="Telefon osoby">
                                            <Input prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={16}>
                                    <Col span={24}>
                                        <Form.Item name={['primaryContact', 'role']} label="Stanowisko / Rola">
                                            <Input placeholder="np. Właściciel, Zaopatrzeniowiec" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Panel>
                        </Collapse>
                    </div>
                )}

                <Collapse defaultActiveKey={[]} ghost>
                    <Panel header="Adresy i warunki" key="2">
                        <Divider orientation="left" plain style={{ marginTop: 0 }}>Adres do faktury</Divider>
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={12}>
                                <Form.Item name={['invoiceAddress', 'street']} label="Ulica i numer">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={6} md={6}>
                                <Form.Item name={['invoiceAddress', 'postalCode']} label="Kod pocztowy">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={6} md={6}>
                                <Form.Item name={['invoiceAddress', 'city']} label="Miasto">
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left" plain>Adres dostawy</Divider>
                        <Row gutter={16}>
                            <Col xs={24} sm={12} md={12}>
                                <Form.Item name={['deliveryAddress', 'street']} label="Ulica i numer">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={6} md={6}>
                                <Form.Item name={['deliveryAddress', 'postalCode']} label="Kod pocztowy">
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col xs={12} sm={6} md={6}>
                                <Form.Item name={['deliveryAddress', 'city']} label="Miasto">
                                    <Input />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left" plain>Warunki i uwagi</Divider>
                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Form.Item name="paymentTerms" label="Warunki płatności">
                                    <Input placeholder="np. 14 dni" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="fulfillmentNotes" label="Uwagi do realizacji">
                            <TextArea rows={3} />
                        </Form.Item>
                    </Panel>
                </Collapse>

                <div style={{ textAlign: 'right', marginTop: 24 }}>
                    <Button onClick={onClose} style={{ marginRight: 8, width: isMobile ? 'calc(50% - 4px)' : 'auto' }}>
                        Anuluj
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading} style={{ width: isMobile ? 'calc(50% - 4px)' : 'auto' }}>
                        Zapisz
                    </Button>
                </div>
            </Form>
        </Modal>
    );
};
